
import { supabase } from "@/integrations/supabase/client";

export interface AccountOwnershipInfo {
  isOwner: boolean;
  accountOwnerUserId: string | null;
  canEdit: boolean;
  canView: boolean;
}

// Get comprehensive account ownership information
export const getAccountOwnershipInfo = async (companyId: string): Promise<AccountOwnershipInfo> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return {
        isOwner: false,
        accountOwnerUserId: null,
        canEdit: false,
        canView: false
      };
    }

    // Get account owner
    const { data: accountData, error: accountError } = await supabase
      .from('accounts')
      .select('user_id')
      .eq('company_id', companyId)
      .single();

    if (accountError) {
      console.error('Error fetching account:', accountError);
      return {
        isOwner: false,
        accountOwnerUserId: null,
        canEdit: false,
        canView: false
      };
    }

    const accountOwnerUserId = accountData.user_id;
    const isOwner = accountOwnerUserId === user.id;

    // If user is the owner, they can view and edit
    if (isOwner) {
      return {
        isOwner: true,
        accountOwnerUserId,
        canEdit: true,
        canView: true
      };
    }

    // Check if user is a supervisor of the account owner
    const { data: subordinates, error: subordinatesError } = await supabase
      .rpc('get_user_subordinates', { supervisor_uuid: user.id });

    if (subordinatesError) {
      console.error('Error fetching subordinates:', subordinatesError);
      return {
        isOwner: false,
        accountOwnerUserId,
        canEdit: false,
        canView: false
      };
    }

    // Check if account owner is in the subordinates list
    const isSupervisor = subordinates && subordinates.some(
      (sub: any) => sub.subordinate_id === accountOwnerUserId
    );

    return {
      isOwner: false,
      accountOwnerUserId,
      canEdit: false, // Only owners can edit
      canView: isSupervisor // Only supervisors (and owners) can view
    };

  } catch (error) {
    console.error('Error getting account ownership info:', error);
    return {
      isOwner: false,
      accountOwnerUserId: null,
      canEdit: false,
      canView: false
    };
  }
};

// Check if user can access the account (as owner or supervisor)
export const checkAccountAccess = async (companyId: string): Promise<boolean> => {
  try {
    const ownershipInfo = await getAccountOwnershipInfo(companyId);
    return ownershipInfo.canView;
  } catch (error) {
    console.error('Error checking account access:', error);
    return false;
  }
};

// Get account owner's user ID
export const getAccountOwnerUserId = async (companyId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('accounts')
      .select('user_id')
      .eq('company_id', companyId)
      .single();
    
    if (error) {
      console.error('Error fetching account owner:', error);
      return null;
    }
    
    return data?.user_id || null;
  } catch (error) {
    console.error('Error in getAccountOwnerUserId:', error);
    return null;
  }
};
